import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import CustomVideoPlayer from '../components/CustomVideoPlayer';
import { Link } from 'react-router-dom';
import { podcastApi, API_BASE_URL } from '../services/api';
import SocialShareDialog from '../components/SocialShareDialog';

// Helper function to update meta tags
const updateMetaTag = (property, content) => {
  let selector = `meta[property="${property}"]`;
  if (!document.querySelector(selector)) {
    selector = `meta[name="${property}"]`;
  }
  
  let element = document.querySelector(selector);
  if (element) {
    element.setAttribute('content', content);
  } else {
    element = document.createElement('meta');
    if (property.startsWith('og:') || property.startsWith('twitter:')) {
      element.setAttribute('property', property);
    } else {
      element.setAttribute('name', property);
    }
    element.setAttribute('content', content);
    document.getElementsByTagName('head')[0].appendChild(element);
  }
};

const PodcastPublicPage = () => {
  const { shareId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const shareUrl = data ? `${window.location.origin}/share/${data.shareId}` : '';

  const handleShare = async () => {
    const mobile = /Mobi|Android|iPhone|iPad|iPod/i.test(navigator.userAgent);
    const shareData = {
      title: data.title,
      text: data.title,
      url: shareUrl
    };
    try {
      if (navigator.share && mobile) {
        await navigator.share(shareData);
      } else {
        setShareOpen(true);
      }
    } catch (error) {
      console.error('Error sharing:', error);
      setShareOpen(true);
    }
  };

  useEffect(() => {
    let mounted = true;
    const fetchData = async () => {
      try {
        setLoading(true);
        const res = await podcastApi.getPublicPodcast(shareId);
        if (!mounted) return;
        setData(res);
        setError(null);
        
        // Set dynamic meta tags for social sharing
        document.title = `${res.title} - Postgame Buzz`;
        
        // Update Open Graph meta tags
        updateMetaTag('og:title', res.title);
        updateMetaTag('og:description', res.title);
        updateMetaTag('og:image', `${window.location.origin}/images/og-default.png`);
        updateMetaTag('og:image:type', 'image/png');
        updateMetaTag('og:image:width', '1200');
        updateMetaTag('og:image:height', '630');
        updateMetaTag('og:url', window.location.href);
        updateMetaTag('og:type', 'website');
        
        // Update Twitter Card meta tags
        updateMetaTag('twitter:card', 'summary');
        updateMetaTag('twitter:title', res.title);
        updateMetaTag('twitter:description', res.title);
        updateMetaTag('twitter:image', `${window.location.origin}/images/og-default.png`);
        
      } catch (e) {
        if (!mounted) return;
        const message = e?.response?.data?.message || 'Podcast not found or is not public.';
        setError(message);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    fetchData();
    return () => { mounted = false; };
  }, [shareId]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-400 mx-auto mb-3"></div>
          <p className="text-gray-400">Loading podcast...</p>
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-white mb-2">Oops</h1>
          <p className="text-gray-400 mb-6">{error || 'Unable to load this podcast.'}</p>
          <Link className="text-blue-400 hover:underline font-semibold" to="/">Go to homepage</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-blue-950 to-slate-800">
      {/* Hero Section with Beautiful Background */}
      <div className="relative overflow-hidden">
        {/* Colorful Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgcGF0dGVyblVuaXRzPSJ1c2VyU3BhY2VPblVzZSIgcGF0dGVyblRyYW5zZm9ybT0icm90YXRlKDQ1KSI+PHJlY3QgeD0iMCIgeT0iMCIgd2lkdGg9IjUwIiBoZWlnaHQ9IjUwIiBmaWxsPSJub25lIiBzdHJva2U9IiM2NDY0ZmYiIHN0cm9rZS13aWR0aD0iMSIvPjwvcGF0dGVybj48L2RlZnM+PHJlY3Qgd2lkdGg9IjEwMCUiIGhlaWdodD0iMTAwJSIgZmlsbD0idXJsKCNwYXR0ZXJuKSIvPjwvc3ZnPg==')]"></div>
        </div>

        <div className="container mx-auto px-4 py-12">
          {/* Main Content Card */}
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-3xl border border-blue-700/30 shadow-2xl">
              <div className="p-6">
                {/* Video Player — top of card */}
                <div className="mb-6">
                  <CustomVideoPlayer 
                    src={`${API_BASE_URL}/api/podcasts/stream-mp4-public/${data.shareId}`}
                    fallbackAudioSrc={`${API_BASE_URL}/api/podcasts/stream-public/${data.shareId}`}
                    title={data.title}
                    downloadHref={data.downloadUrl}
                  />
                </div>

                {/* Title + Meta — below the video */}
                <div className="text-center mb-6">
                  <h1 className="text-xl sm:text-2xl font-black mb-3 text-white leading-snug">
                    {data.opponentName ? `${data.teamName} vs. ${data.opponentName}` : data.title}
                  </h1>

                  <div className="flex items-center justify-center space-x-4 text-gray-300 mb-3 text-sm">
                    <span className="flex items-center space-x-1">
                      <span className="text-yellow-500">⚾</span>
                      <span>{data.teamName}</span>
                    </span>
                    {data.createdAt && (
                      <span className="flex items-center space-x-1">
                        <span className="text-blue-500">📅</span>
                        <span>{new Date(data.createdAt).toLocaleDateString()}</span>
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <div className="inline-block bg-emerald-500/20 border border-emerald-500/40 text-emerald-300 px-3 py-1 rounded-full text-xs font-medium">
                      Generated by AI
                    </div>
                    <div className="inline-block bg-green-500/20 border border-green-500/40 text-green-300 px-3 py-1 rounded-full text-xs font-medium">
                      Public
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex flex-col sm:flex-row gap-3 mb-8">
                  <button
                    onClick={handleShare}
                    className="group inline-flex items-center justify-center space-x-3 bg-gradient-to-r from-emerald-600 to-emerald-700 hover:from-emerald-500 hover:to-emerald-600 text-white font-bold py-3 px-6 rounded-xl transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl border border-emerald-500/20 flex-1"
                  >
                    <svg className="h-5 w-5 group-hover:scale-110 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                    </svg>
                    <span>Share Podcast</span>
                  </button>

                  <Link
                    to="/create"
                    className="group inline-flex items-center justify-center space-x-3 bg-gradient-to-r from-gray-800 via-yellow-600 to-black text-white font-bold py-3 px-6 rounded-xl hover:from-black hover:via-yellow-500 hover:to-gray-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl border border-yellow-600/20 flex-1"
                  >
                    <svg className="h-5 w-5 group-hover:rotate-12 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                    </svg>
                    <span>Create Your Own</span>
                  </Link>
                </div>

                {/* Feature Cards Section */}
                <div className="grid md:grid-cols-3 gap-8 mb-12">
                  {/* Feature 1 */}
                  <div className="group text-center p-8 bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/50 relative overflow-hidden">
                    
                    {/* Colorful gradient icon container */}
                    <div className="relative z-10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 overflow-hidden group mx-auto">
                      {/* Animated gradient background */}
                      <div className="absolute inset-0 rounded-2xl opacity-90 group-hover:opacity-100 transition-all duration-500 bg-gradient-to-br from-blue-500 to-cyan-400"></div>
                      
                      {/* Icon with subtle shadow */}
                      <div className="relative z-10 transform group-hover:scale-110 transition-transform duration-300">
                        <svg className="h-7 w-7 text-white drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                      </div>
                      
                      {/* Shine effect on hover */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-3 relative z-10">Lightning Fast</h3>
                    <p className="text-gray-400 relative z-10">Generate professional podcasts in just minutes</p>
                  </div>

                  {/* Feature 2 */}
                  <div className="group text-center p-8 bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/50 relative overflow-hidden">
                    
                    {/* Colorful gradient icon container */}
                    <div className="relative z-10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 overflow-hidden group mx-auto">
                      {/* Animated gradient background */}
                      <div className="absolute inset-0 rounded-2xl opacity-90 group-hover:opacity-100 transition-all duration-500 bg-gradient-to-br from-yellow-500 to-amber-400"></div>
                      
                      {/* Icon with subtle shadow */}
                      <div className="relative z-10 transform group-hover:scale-110 transition-transform duration-300">
                        <svg className="h-7 w-7 text-white drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                        </svg>
                      </div>
                      
                      {/* Shine effect on hover */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-3 relative z-10">AI-Powered</h3>
                    <p className="text-gray-400 relative z-10">Advanced AI creates engaging sports commentary</p>
                  </div>

                  {/* Feature 3 */}
                  <div className="group text-center p-8 bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm rounded-2xl border border-blue-700/30 shadow-xl hover:shadow-2xl transition-all duration-300 hover:-translate-y-1 hover:border-blue-500/50 relative overflow-hidden">
                    
                    {/* Colorful gradient icon container */}
                    <div className="relative z-10 w-16 h-16 rounded-2xl flex items-center justify-center mb-6 overflow-hidden group mx-auto">
                      {/* Animated gradient background */}
                      <div className="absolute inset-0 rounded-2xl opacity-90 group-hover:opacity-100 transition-all duration-500 bg-gradient-to-br from-purple-500 to-pink-400"></div>
                      
                      {/* Icon with subtle shadow */}
                      <div className="relative z-10 transform group-hover:scale-110 transition-transform duration-300">
                        <svg className="h-7 w-7 text-white drop-shadow-sm" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                        </svg>
                      </div>
                      
                      {/* Shine effect on hover */}
                      <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                    
                    <h3 className="text-xl font-bold text-white mb-3 relative z-10">Easy Sharing</h3>
                    <p className="text-gray-400 relative z-10">Share your podcasts instantly on any platform</p>
                  </div>
                </div>

                {/* Call to Action Section */}
                <div className="relative overflow-hidden bg-gradient-to-br from-blue-900/60 to-slate-800/80 backdrop-blur-sm p-12 rounded-3xl border border-blue-700/30 shadow-2xl text-center">
                  {/* Animated background pattern */}
                  <div className="absolute inset-0 opacity-5">
                    <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciPjxkZWZzPjxwYXR0ZXJuIGlkPSJwYXR0ZXJuIiB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHBhdHRlcm5Vbml0cz0idXNlclNwYWNlT25Vc2UiPjxjaXJjbGUgY3g9IjMwIiBjeT0iMzAiIHI9IjIiIGZpbGw9IiM2MzY2ZjEiLz48L3BhdHRlcm4+PC9kZWZzPjxyZWN0IHdpZHRoPSIxMDAlIiBoZWlnaHQ9IjEwMCUiIGZpbGw9InVybCgjcGF0dGVybikiLz48L3N2Zz4=')]"></div>
                  </div>
                  
                  {/* Gradient border effect */}
                  <div className="absolute inset-0 rounded-3xl bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 opacity-20 blur-sm"></div>
                  <div className="absolute inset-0.5 rounded-3xl bg-gradient-to-br from-blue-900/60 to-slate-800/80"></div>
                  
                  <div className="relative z-10">
                    <h3 className="text-4xl font-black text-white mb-4">Ready to Create Your Own?</h3>
                    <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">Transform your game into a professional sports podcast with AI-powered commentary</p>
                    
                    <div className="relative inline-block group">
                      {/* Button glow effect */}
                      <div className="absolute -inset-1 bg-gradient-to-r from-gray-800 via-yellow-600 to-black rounded-xl blur opacity-30 group-hover:opacity-60 transition duration-300"></div>
                      
                      <a 
                        href={`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:3001'}/auth/google`}
                        className="relative inline-flex items-center space-x-3 bg-gradient-to-r from-gray-800 via-yellow-600 to-black text-white font-bold py-4 px-8 rounded-xl text-xl hover:from-black hover:via-yellow-500 hover:to-gray-800 transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl border border-yellow-600/20"
                      >
                        <div className="relative">
                          <div className="absolute -inset-1 bg-yellow-400/30 rounded-full blur-md group-hover:bg-yellow-400/50 transition-all duration-300"></div>
                          <img 
                            src="/logo.svg" 
                            alt="Postgame Buzz Logo" 
                            className="relative h-6 w-6 z-10 group-hover:scale-110 transition-transform duration-300"
                          />
                        </div>
                        <span>Create Your Podcast Now</span>
                        
                        {/* Shine effect */}
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/10 to-transparent -skew-x-12 opacity-0 group-hover:opacity-100 group-hover:animate-pulse transition-opacity duration-500 rounded-xl"></div>
                      </a>
                    </div>
                    
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <SocialShareDialog
        open={shareOpen}
        onClose={() => setShareOpen(false)}
        shareUrl={shareUrl}
        title={data ? data.title : ''}
        text={data ? data.title : ''}
      />
    </div>
  );
};

export default PodcastPublicPage;
